import { useState } from 'react';
import api from '../services/api';

/**
 * @module useSendWeeklyReports
 * @description Custom hook for sending weekly reports in the Mental Health Portal application.
 * Manages sending state, error handling, and provides a function for sending weekly reports.
 *
 * @requires react
 * @requires ../services/api
 */

/**
 * Hook to send weekly reports
 * @function useSendWeeklyReports
 * @returns {Object} Object containing sendWeeklyReports function, isSending state, and error state
 */
export const useSendWeeklyReports = () => {
  /**
   * @state {boolean} isSending - Indicates if the send operation is in progress
   */
  const [isSending, setIsSending] = useState(false);

  /**
   * @state {string|null} error - Error message if the send operation fails, null otherwise
   */
  const [error, setError] = useState(null);

  /**
   * Sends all reviewed weekly reports
   * @async
   * @function sendWeeklyReports
   * @returns {Promise<Object>} The response data from the API
   * @throws {Error} If the send operation fails
   */
  const sendWeeklyReports = async () => {
    setIsSending(true);
    setError(null);

    try {
      const { data } = await api.post('send-weekly-report');
      return data;
    } catch (err) {
      console.error('Erro ao enviar relatórios semanais:', err);
      setError('Erro ao enviar relatórios semanais');
      throw err;
    } finally {
      setIsSending(false);
    }
  };

  /**
   * @typedef {Object} UseSendWeeklyReportsReturn
   * @property {Function} sendWeeklyReports - Function to send weekly reports
   * @property {boolean} isSending - Indicates if the send operation is in progress
   * @property {string|null} error - Error message if the send operation fails, null otherwise
   */

  /**
   * @returns {UseSendWeeklyReportsReturn} The hook's return values
   */
  return { sendWeeklyReports, isSending, error };
};
